import { colourIds } from './palette';
import type { BlockSelection, MapartPalette } from './types';

export const PRESET_FILE_VERSION = 1;
export const PRESET_FILE_EXTENSION = '.mapart-presets.json';

const STORAGE_KEY = 'vektor.mapart.presets';

export interface BlockPreset {
  id: string;
  name: string;
  updatedAt: string;
  selection: BlockSelection;
}

interface PresetFile {
  version: number;
  presets: BlockPreset[];
}

/** Drops colours the palette does not have and block indices it cannot resolve. */
export function sanitiseSelection(palette: MapartPalette, selection: BlockSelection): BlockSelection {
  const clean: BlockSelection = {};
  for (const id of colourIds(palette)) {
    const index = selection[id];
    if (index === null || index === undefined) continue;
    if (!Number.isInteger(index) || index < 0 || index >= palette.colors[id].blocks.length) continue;
    clean[id] = index;
  }
  return clean;
}

export function enabledCount(selection: BlockSelection): number {
  return Object.values(selection).filter((index) => index !== null && index !== undefined).length;
}

export function loadPresets(): BlockPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as BlockPreset[];
    return Array.isArray(parsed) ? parsed.filter(isPreset) : [];
  } catch {
    return [];
  }
}

export function storePresets(presets: BlockPreset[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch {
    /* private mode or quota */
  }
}

export function upsertPreset(presets: BlockPreset[], preset: BlockPreset): BlockPreset[] {
  const without = presets.filter((existing) => existing.id !== preset.id);
  return [preset, ...without].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function removePreset(presets: BlockPreset[], id: string): BlockPreset[] {
  return presets.filter((preset) => preset.id !== id);
}

export function serialisePresets(presets: BlockPreset[]): string {
  const file: PresetFile = { version: PRESET_FILE_VERSION, presets };
  return JSON.stringify(file, null, 2);
}

export function parsePresets(text: string): BlockPreset[] {
  const file = JSON.parse(text) as PresetFile;
  if (file.version !== PRESET_FILE_VERSION) {
    throw new Error(`Unsupported preset file version ${file.version}`);
  }
  if (!Array.isArray(file.presets) || !file.presets.every(isPreset)) {
    throw new Error('Preset file holds no usable presets');
  }
  return file.presets;
}

function isPreset(value: unknown): value is BlockPreset {
  if (!value || typeof value !== 'object') return false;
  const preset = value as BlockPreset;
  return (
    typeof preset.id === 'string' &&
    typeof preset.name === 'string' &&
    typeof preset.updatedAt === 'string' &&
    !!preset.selection &&
    typeof preset.selection === 'object'
  );
}
